import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { CDN_URL, BUCKET_NAME, PRODUCTS_IMAGES_FOLDER } from "../../../shared/utils/constants";
import { addToWishlist } from "../../wishlist/api";
import { AuthContext } from "../../auth/context/AuthContext";
import { getImageUrl } from "../../../shared/utils/imageUtils";
import { IMAGE_PRESETS } from "../../../shared/utils/imagePresets";
import { decodeEntities, getPricing } from "../utils/productDisplay";

/** The product card used by every product grid on the site. */
const ProductCard = ({ product }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const [isWishlistSubmitting, setIsWishlistSubmitting] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const name = decodeEntities(product.name);
  const { price, original, discountPercentage } = getPricing(product);
  const detailsUrl = `/product-details/${product.slug}`;

  const handleAddToWishlist = async () => {
    setIsWishlistSubmitting(true);
    try {
      await addToWishlist(product.id);
      window.dispatchEvent(new Event("wishlistUpdated"));
      toast.success("Item added to wishlist");
    } catch (error) {
      console.error(error);
      const errorMessage =
        error?.response?.data || "An unexpected error occurred";
      toast.error(`Error adding to wishlist: ${errorMessage}`);
    } finally {
      setIsWishlistSubmitting(false);
    }
  };

  return (
    <div className="product-card border border-gray-100 hover-border-main-600 rounded-16 position-relative transition-2">
      {product.configuration?.Sale?.configStatus === "active" && (
        <span className="badge bg-main-600 text-white text-sm fw-semibold position-absolute top-0 start-0">
          {product.configuration.Sale.collectionDisplayName}
        </span>
      )}
      {product.configuration?.Free_Delivery?.configStatus === "active" && (
        <span className="badge bg-success-50 text-success-600 position-absolute bottom-0 end-0">
          Free Delivery
          <i className="ph ph-truck" />
        </span>
      )}

      {isAuthenticated && !product.nonCod && (
        <button
          disabled={isWishlistSubmitting}
          type="button"
          onClick={handleAddToWishlist}
          aria-label="Add to wishlist"
          className={`product-card__cart btn bg-main-50 text-main-600 hover-bg-main-600 hover-text-white py-11 px-11 rounded-pill flex-align gap-8 position-absolute inset-block-start-0 inset-inline-end-0 me-16 mt-16 ${
            isWishlistSubmitting ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isWishlistSubmitting ? (
            <i className="ph ph-spinner animate-spin" />
          ) : (
            <i className="ph ph-heart" />
          )}
        </button>
      )}

      <Link to={detailsUrl} className="product-card__thumb flex-center">
        {product.images?.[0] && !imageFailed ? (
          <img
            src={getImageUrl({
              cdnUrl: CDN_URL,
              bucket: BUCKET_NAME,
              key: `${PRODUCTS_IMAGES_FOLDER}/${product.images[0]}`,
              transforms: IMAGE_PRESETS.THUMBNAIL_SQUARE,
            })}
            alt={name}
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <span
            className="product-card__no-image w-100 h-100 flex-center bg-gray-50 text-gray-300"
            aria-label={name}
            role="img"
          >
            <i className="ph ph-image text-5xl" aria-hidden="true" />
          </span>
        )}
      </Link>

      <div className="product-card__content mt-6">
        <h6 className="title text-sm fw-semibold">
          <Link to={detailsUrl} className="link text-line-2" title={name}>
            {name}
          </Link>
        </h6>

        <div className="product-card__price">
          <span className="product-card__amount">LKR {price.toFixed(2)}</span>
          {discountPercentage > 0 && (
            <div className="product-card__was">
              <span className="text-decoration-line-through">
                LKR {original.toFixed(2)}
              </span>
              <span className="product-card__off">-{discountPercentage}%</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
